import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

type LanguageSwitchPresence = { register: () => () => void };
const LanguageSwitchContext = createContext<LanguageSwitchPresence | null>(null);

/** Keep existing header choices; provide one global choice on all other routes. */
export function LanguageSwitchProvider({ children }: { children: ReactNode }) {
  const [inlineCount, setInlineCount] = useState(0);
  const register = useCallback(() => {
    setInlineCount(count => count + 1);
    return () => setInlineCount(count => Math.max(0, count - 1));
  }, []);
  const value = useMemo(() => ({ register }), [register]);

  return (
    <LanguageSwitchContext.Provider value={value}>
      {children}
      {inlineCount === 0 && <LanguageSwitcher floating />}
    </LanguageSwitchContext.Provider>
  );
}

type LanguageSwitcherProps = {
  className?: string;
  floating?: boolean;
};

/** A local UI preference only: no navigation, form submission or tracking event. */
export function LanguageSwitcher({ className, floating = false }: LanguageSwitcherProps) {
  const { lang, toggleLang } = useLanguage();
  const presence = useContext(LanguageSwitchContext);
  const register = presence?.register;
  useEffect(() => {
    if (!floating && register) return register();
  }, [floating, register]);

  return (
    <button
      type="button"
      onClick={toggleLang}
      aria-label={lang === "en" ? "Switch to Arabic" : "التبديل إلى الإنجليزية"}
      title={lang === "en" ? "العربية" : "English"}
      data-language-switch="true"
      data-language-placement={floating ? "floating" : "header"}
      className={cn(
        "inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B6914] focus-visible:ring-offset-2 active:scale-[0.97]",
        floating && "website-language-choice fixed z-50 border border-[#D4A843]/35 bg-[#FFFDF8]/95 text-[#5C5549] shadow-sm backdrop-blur-sm hover:bg-[#F5EDDF]",
        className,
      )}
    >
      <Globe className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span lang={lang === "en" ? "ar" : "en"} dir={lang === "en" ? "rtl" : "ltr"} className="whitespace-nowrap">
        {lang === "en" ? "عربي" : "English"}
      </span>
    </button>
  );
}
